import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useContext, useEffect, useState } from "react";
import { graphQlClient } from "../../../shared/clients";
import SettingsSection from "../../../shared/components/SettingsSection";
import {
  ActionButton,
  TableActions,
  TableCell,
  TableRow,
} from "../../../shared/components/table/StyledTable";
import { QUERY_GET_ALL_TASKS } from "../../../shared/gql/queries";
import { isSure } from "../../../shared/helpers";
import ConfigDataProvider, {
  ConfigDataContext,
} from "../../../shared/providers/ConfigDataProvider";
import { GET_CONFIG_DATA, SET_CONFIG_DATA } from "../../../shared/reducer";
import { logger, notify } from "../../../shared/utils";
import TaskCategoriesDialog from "./TaskCategoriesDialog";

/* Tasks/ConfigData work together */
const TaskCategories = () => (
  <ConfigDataProvider>
    <ConsumerTaskCategories />
  </ConfigDataProvider>
);

const ConsumerTaskCategories = () => {
  const configData = useContext(ConfigDataContext);
  const taskCategories = (configData.state?.data as any[]) ?? [];

  useEffect(() => {
    configData.dispatch({
      type: GET_CONFIG_DATA,
      id: "TaskConfigData",
      private: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dialog, setDialog] = useState<{ open: boolean; id?: string }>({
    open: false,
  });

  const handleOpenDialog = (id?: string) =>
    setDialog({ open: true, ...(id && { id }) });
  const handleCloseDialog = () => setDialog({ open: false });

  const handleDeleteCategory = async (idToDelete: string) => {
    try {
      const { items } = await graphQlClient(QUERY_GET_ALL_TASKS, {
        filter: { taskId: idToDelete },
      });

      // Tasks may be assigned this category
      const isAssignedToExistingTasks = !!items.length;
      if (isAssignedToExistingTasks) {
        notify(
          "Diese Kategorie ist mit bestehenden Aufgaben verknüpft. Bitte kontaktiere die IT-Abteilung.",
        );
        return;
      }

      if (!(await isSure())) return;

      // Filter category from TaskConfigData[]
      const newData = taskCategories.filter(
        (taskConfig) => taskConfig.id !== idToDelete,
      );

      // Spread loaded configData (id, publicAccess) w/ new TaskConfigData[]
      const payload = {
        ...configData.state,
        data: newData,
      };

      // Mutate configData
      configData.dispatch({
        type: SET_CONFIG_DATA,
        payload,
      });
    } catch (err) {
      logger(
        `TaskCategories: error thrown when attempting to delete { TaskConfigData.id: ${idToDelete}`,
      );
      notify(
        "Fehler! Bitte kontaktieren Sie die IT-Abteilung mit dem Aufgabentyp", "error");
    }
  };

  return (
    <>
      <SettingsSection
        title="Aufgabentypen"
        description="Kategorien, die Aufgaben zugeordnet werden können"
        columns={["Aufgabentyp"]}
        onAdd={() => handleOpenDialog()}
        addLabel="Aufgabentyp"
        isEmpty={!taskCategories.length}
        emptyMessage="Noch keine Aufgabentypen angelegt."
      >
        {taskCategories.map(({ id, text }) => (
          <TableRow key={id}>
            <TableCell sx={{ fontWeight: 500 }}>{text}</TableCell>
            <TableCell align="right">
              <TableActions>
                <ActionButton
                  icon={<EditIcon />}
                  label="Aufgabentyp bearbeiten"
                  onClick={() => handleOpenDialog(id)}
                />
                <ActionButton
                  icon={<DeleteIcon />}
                  label="Aufgabentyp löschen"
                  onClick={() => handleDeleteCategory(id)}
                />
              </TableActions>
            </TableCell>
          </TableRow>
        ))}
      </SettingsSection>

      {dialog.open && (
        <TaskCategoriesDialog
          id={dialog.id}
          handleCloseDialog={handleCloseDialog}
        />
      )}
    </>
  );
};

export default TaskCategories;
